import React, { useState, useEffect } from "react";
import Table from "../../components/Table/Table";
import TopNavbar from "../../components/TopNavbar/TopNavbar";
import ApiManager from "../../ApiManager/ApiManager";
import NewExpenditure from "./NewExpenditure";
import FilterBar from "../../components/FilterBar/FilterBar";
import { useAuth } from "../../components/AuthProvider";
import { hasPermission, ShowButton } from "../../components/RoleBasedComponents";
import PERMISSIONS from "../../permissions";
import Modal from "../../components/Modal/Modal";
import { prepareFileForModal } from "../../utils/fileUtils";
import FileViewer from "../../components/FileViewer/FileViewer";
import EditExpenditure from "./EditExpenditure";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import {  toast } from "react-toastify";

function Expenditures() {
  const { userPermissions } = useAuth();
  const {
    loadingStates,
    loadingMessages,
    loadingProgress,
    withLoading
  } = useComponentLoading({
    fetchData: false,
    fileView: false,
    approve: false,
    reject: false
  });

  const [data, setData] = useState([]);
  const [rowId, setRowId] = useState(null);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);
  const [expenditureReceiptStatus, setExpenditureReceiptStatus] = useState("");
  const [showFile, setShowFile] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [isEdit, setEdit] = useState(false);

  const handleFileClick = async (fileName) => {
    try {
      // Set loading state and open modal immediately
      setModalOpen(true);
      setShowFile(true);
      setEdit(false);
      setFileUrl(null);
      setFileType(null);

      await withLoading('fileView', async (updateProgress) => {
        // Use the utility function to prepare the file for viewing in the modal
        await prepareFileForModal(fileName, setFileUrl, setFileType, updateProgress);

        updateProgress(100);
      }, 'Loading file...', 30000);
    } catch (error) {
      console.error("Error fetching file:", error);
      setModalOpen(false);
      setShowFile(false);
      toast.error("Failed to load file. Please try again.");
    }
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setShowFile(false);
    setEdit(false);
  };

  const handleEdit = () => {
    setEdit(true);
    setModalOpen(true);
    setShowFile(false);
  };

  const handleApproved = async () => {
    try {
      await withLoading('approve', async (updateProgress) => {
        updateProgress(30);

        const response = await ApiManager.markExpenditureApproved(
          new URLSearchParams({ id: rowId })
        );

        updateProgress(70);

        if (!response.ok) throw new Error("Network response was not ok");
        setExpenditureReceiptStatus("approved");
        fetchData();

        updateProgress(100);
      }, 'Approving expenditure...', 30000);
    } catch (error) {
      console.error("Error approving expenditure:", error);
    }
  };

  const handleRejected = async () => {
    try {
      await withLoading('reject', async (updateProgress) => {
        updateProgress(30);

        const response = await ApiManager.markExpenditureRejected(
          new URLSearchParams({ id: rowId })
        );

        updateProgress(70);

        if (!response.ok) throw new Error("Network response was not ok");
        setExpenditureReceiptStatus("rejected");
        fetchData();

        updateProgress(100);
      }, 'Rejecting expenditure...', 30000);
    } catch (error) {
      console.error("Error rejecting expenditure:", error);
    }
  };

  const buttons = [];

  if (hasPermission(userPermissions, PERMISSIONS.VENDOR_EXPENDITURE_APPROVED_UPDATE)) {
    buttons.push({ label: "Approve", onClick: () => handleApproved() });
  }

  if (hasPermission(userPermissions, PERMISSIONS.VENDOR_EXPENDITURE_REJECTED_UPDATE)) {
    buttons.push({ label: "Reject", onClick: () => handleRejected() });
  }

  if (hasPermission(userPermissions, PERMISSIONS.VENDOR_EXPENDITURE_UPDATE)) {
    buttons.push({ label: "Edit", onClick: () => handleEdit() });
  }

  useEffect(() => {
    fetchData();
  }, [filters, currentPage, expenditureReceiptStatus]);

  const fetchData = async () => {
    try {
      await withLoading('fetchData', async (updateProgress) => {
        updateProgress(10);

        const params = new URLSearchParams({
          from: filters.startTime ? new Date(filters.startTime).getTime() / 1000 : 0,
          to: filters.endTime ? new Date(filters.endTime).getTime() / 1000 : 1993456000,
          page_size: 20,
          page: currentPage,
          driverId: filters.driverId || "",
          carId: filters.carId || "",
          status: filters.status || "",
        });

        updateProgress(30);

        const response = await ApiManager.getAllExpenditures(params);
        if (!response.ok) throw new Error("Failed to fetch expenditures");

        updateProgress(60);

        const json = await response.json();

        updateProgress(90);

        if (JSON.stringify(json) !== JSON.stringify(data)) {
          setData(json || []);
          setHasMorePages(json.length === 20);
        }

        updateProgress(100);
      }, 'Loading expenditures...', 30000);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setHasMorePages(false);
    }
  };

  const columns = [
    { field: "driver_name", headerName: "Driver" },
    { field: "car_number", headerName: "Car Number" },
    { field: "amount", headerName: "Amount" },
    { field: "is_paid_by_driver", headerName: "Driver Paid" },
    { field: "is_car_related", headerName: "Car Related" },
    { field: "description", headerName: "Description" },
    { field: "status", headerName: "Status" },
    {
      field: "filepath",
      headerName: "Receipt",
      renderCell: (params) => {
        if (!params.row.filepath) return "N/A";

        return (
          <ShowButton permission={PERMISSIONS.VENDOR_FILE_READ}>
            <button
              onClick={() => handleFileClick(params.row.filepath)}
              className="custom-button-green"
            >
              View Receipt
            </button>
          </ShowButton>
        );
      },
    },
  ];

  const view_columns = [
    { field: "driver_name", headerName: "Driver" },
    { field: "car_number", headerName: "Car Number" },
    { field: "amount", headerName: "Amount" },
    { field: "is_paid_by_driver", headerName: "Driver Paid" },
    { field: "is_car_related", headerName: "Car Related" },
    { field: "description", headerName: "Description" },
    { field: "status", headerName: "Status" },
  ];

  const rows = (data || []).map((item) => ({
    id: item.id,
    driver_name: item.driver_name,
    car_number: item.car_number,
    amount: item.amount,
    is_paid_by_driver: item.is_paid_by_driver,
    is_car_related: item.is_car_related,
    filepath: item.filepath,
    description: item.description,
    status: item.status == "created" ? "Pending" : item.status,
  }));

  const filterOptions = [
    { value: "driver", label: "Driver" },
    { value: "car", label: "Car" },
    { value: 'time', label: 'Time Range' },
    { value: 'status', label: 'Status' }
  ];

  const apiFunctions = {
    driver: ApiManager.getAllDrivers,
    car: ApiManager.getAllCars,
  };
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };


  const handleFieldUpdate = async ({ rowId, field, newValue }) => {
    if(newValue === "approved") {
      try {
        await withLoading('approve', async (updateProgress) => {
          updateProgress(30);

          const response = await ApiManager.markExpenditureApproved(
            new URLSearchParams({ id: rowId })
          );

          updateProgress(70);

          if (!response.ok) throw new Error("Network response was not ok");
          setData((prev) =>
            prev.map((item) => item.id === rowId ? { ...item, [field]: newValue } : item)
          );

          updateProgress(100);
        }, 'Approving expenditure...', 30000);
      } catch (error) {
        console.error("Error approving expenditure:", error);
      }
    }
    else if(newValue === "rejected"){
      try {
        await withLoading('reject', async (updateProgress) => {
          updateProgress(30);

          const response = await ApiManager.markExpenditureRejected(
            new URLSearchParams({ id: rowId })
          );

          updateProgress(70);

          if (!response.ok) throw new Error("Network response was not ok");
          setData((prev) =>
            prev.map((item) => item.id === rowId ? { ...item, [field]: newValue } : item)
          );

          updateProgress(100);
        }, 'Rejecting expenditure...', 30000);
      } catch (error) {
        console.error("Error rejecting expenditure:", error);
      }
    }
  };

  return (
    <div className="expenditure-container">
      {/* Loading Spinner - Only show for non-table operations */}
      {(loadingStates.approve || loadingStates.reject) && (
        <LoadingSpinner
          overlay={true}
          message={
            loadingStates.approve ? loadingMessages.approve :
            loadingStates.reject ? loadingMessages.reject : ""
          }
          progress={
            loadingStates.approve ? loadingProgress.approve :
            loadingStates.reject ? loadingProgress.reject : 0
          }
          showProgress={true}
        />
      )}

      <TopNavbar
        title="Expenditures"
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          paddingTop: "20px",
          height: "5vh",
        }}
      >
        <FilterBar
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          apiFunctions={apiFunctions}
        />
        <ShowButton permission={PERMISSIONS.VENDOR_EXPENDITURE_CREATE}>
          <NewExpenditure onSuccess={fetchData} />
        </ShowButton>
      </div>

      <Table
        table_columns={columns}
        view_columns={columns}
        rows={rows}
        setRowsId={setRowId}
        buttons={buttons}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        hasMorePages={hasMorePages}
        onFieldUpdate={handleFieldUpdate}
        isLoading={loadingStates.fetchData}
        loadingMessage={loadingMessages.fetchData}
        loadingProgress={loadingProgress.fetchData}
      />

      {isModalOpen && showFile && (
        <div className="file-container">
          <Modal isOpen={isModalOpen} onClose={handleModalClose}>
            {loadingStates.fileView ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '500px',
                width: '100%'
              }}>
                <LoadingSpinner
                  overlay={false}
                  message={loadingMessages.fileView}
                  progress={loadingProgress.fileView}
                  showProgress={true}
                />
              </div>
            ) : fileUrl ? (
              <div style={{ width: "100%", height: "500px" }}>
                <FileViewer
                  fileUrl={fileUrl}
                  fileType={fileType}
                  fileName={fileUrl ? fileUrl.split('/').pop() : 'expenditure-receipt'}
                />
              </div>
            ) : (
              <p>Loading file...</p>
            )}
          </Modal>
        </div>
      )}

      {isModalOpen && isEdit && (
        <div className="edit-container">
          <Modal isOpen={isModalOpen} onClose={handleModalClose} title="Edit Expenditure">
            <EditExpenditure rowId={rowId} onSuccess={fetchData} toggleFunction={toggleModal}/>
          </Modal>
        </div>
      )}
    </div>
  );
}

export default Expenditures;
